import { MovementTruthOutbox } from '../janus-engine/movement/client-truth-outbox';
import type { ActorTruthCommandBase, MovementTruthTerminalInfo } from '../janus-engine/movement/client-truth-commit';
import type { AppliedSaveInput } from '../shared/applied-save-queue';
import { actorOutbox, actorRowFor, bankConfirmedRev } from './movement-truth.js';

type Answer = { ok: boolean; status?: number; error?: string };
const rowOnly = new Map<string, MovementTruthOutbox<ActorTruthCommandBase>>();

function outbox(encounterId: string, actorId: string): MovementTruthOutbox<ActorTruthCommandBase> {
  if (encounterId && actorRowFor(encounterId, actorId)) return actorOutbox(encounterId, actorId);
  const key = encounterId + ':' + actorId;
  let queue = rowOnly.get(key);
  if (!queue) {
    const row = { encounter_creatures: {} };
    queue = new MovementTruthOutbox({ encounterId, actorId, row: () => row,
      render: () => {}, onRenderError: () => {}, commit: async () => {}, accepted: () => {}, rejected: () => {}, changed: () => {} });
    rowOnly.set(key, queue);
  }
  return queue;
}

/** Reserve each affected actor's existing queue in one task. The one immutable operation
 * sends only after every earlier save has settled; subsequent actions cannot overtake it. */
export function queueActorSave(input: AppliedSaveInput): void {
  const ids = [...new Set(input.actorIds)].sort();
  if (!ids.length) throw new Error('Applied save needs an actor');
  let release!: () => void;
  const ready = new Promise<void>(resolve => { release = resolve; });
  let arrived = 0, cancelled: MovementTruthTerminalInfo | undefined;
  let sending: Promise<Answer> | undefined, corrected: Promise<void> | undefined;
  const failed = (info: MovementTruthTerminalInfo): Promise<void> => {
    cancelled ??= info;
    release();
    return corrected ??= Promise.resolve().then(() => input.refused(info));
  };
  for (const id of ids) outbox(input.encounterId, id).enqueueSave(input.command, {
    commit: async () => {
      if (++arrived === ids.length) release();
      await ready;
      if (cancelled) return { ok: false, ...cancelled };
      if (!sending) sending = input.send().then(answer => {
        if (answer.ok) for (const saved of input.confirmed ?? []) {
          const row = actorRowFor(input.encounterId, saved.actorId);
          if (row) bankConfirmedRev(input.encounterId, saved.actorId, row, saved.rev, saved.aps === undefined ? {} : { aps: saved.aps });
        }
        return answer;
      }).catch(error => { sending = undefined; throw error; });
      return sending;
    }, failed, parked: input.parked,
  });
}

window.ftQueueActorSave = queueActorSave;
for (const input of window.ftPendingActorSaves?.splice(0) ?? []) queueActorSave(input);
